// In Next.js, this file would be called: app/providers.tsx
"use client";

// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import {
  isServer,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { AuthProvider } from "@/contexts/AuthContext";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 5 minutes default — most useQuery callers are read-mostly product
        // data that ISR also caches. Components that need fresher data
        // (e.g. unread counts) already override this with a shorter window.
        staleTime: 5 * 60 * 1000,
        // 30 minutes — keep data in memory long enough for users navigating
        // back to recently visited pages without refetching.
        gcTime: 30 * 60 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

interface ProvidersProps {
  children: React.ReactNode;
  initialRates?: Record<string, unknown>;
}

export default function Providers({ children, initialRates }: ProvidersProps) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <CurrencyProvider initialRates={initialRates}>
        {/* 로그인 상태는 클라이언트에서만 결정한다 — layout 이 쿠키를 읽으면 ISR 이 깨진다
            (contexts/AuthContext 참고). */}
        <AuthProvider>{children}</AuthProvider>
      </CurrencyProvider>
      <Toaster
        richColors
        position="top-center"
        // Safe-area top + 16px gap so the toast clears notched Safari/PWA
        // builds while native iOS can opt out through the shared CSS variable.
        mobileOffset={{
          top: "calc(var(--loc-safe-area-inset-top) + 16px)",
        }}
        offset={{
          top: "calc(var(--loc-safe-area-inset-top) + 16px)",
        }}
      />
    </QueryClientProvider>
  );
}
